import { MemoryVectorStore } from 'langchain/vectorstores/memory';
import { OpenAIEmbeddings } from '@langchain/openai';
import { RecursiveCharacterTextSplitter } from 'langchain/text_splitter';
import { Document } from '@langchain/core/documents';

class RAGSystem {
  constructor() {
    this.vectorStore = null;
    this.embeddings = null;
    this.documents = [];
    this.isInitialized = false;
  }

  async initialize() {
    try {
      const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
      console.log('🔑 Checking API key...', apiKey ? 'Available' : 'Missing');
      
      if (!apiKey) {
        throw new Error('OpenAI API key not found in environment variables');
      }

      // Initialize embeddings - using better model like in working example
      this.embeddings = new OpenAIEmbeddings({
        openAIApiKey: apiKey,
        modelName: 'text-embedding-3-large', // Better quality embeddings
        maxRetries: 3,
      });

      console.log('🧠 RAG system initialized with OpenAI embeddings');
      
      // Test the embeddings with a simple query
      try {
        await this.embeddings.embedQuery('test');
        console.log('✅ Embeddings test successful');
      } catch (testError) {
        console.error('❌ Embeddings test failed:', testError);
        throw new Error('OpenAI API test failed: ' + testError.message);
      }

      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize RAG system:', error);
      this.isInitialized = false;
      return false;
    }
  }

  async processDocument(content, fileName) {
    try {
      console.log('📄 Starting RAG document processing for:', fileName);
      console.log('📄 Content length:', content.length, 'characters');
      
      if (!content || content.length < 10) {
        throw new Error('Document content is too short or empty');
      }

      if (!this.isInitialized) {
        console.log('🔄 RAG system not initialized, initializing...');
        const initialized = await this.initialize();
        if (!initialized) {
          throw new Error('Failed to initialize RAG system');
        }
      }

      console.log('📄 Processing document with RAG:', fileName);

      // Create a single document first (like in working example)
      const document = new Document({
        pageContent: content,
        metadata: {
          source: fileName,
          type: 'uploaded_document',
          totalLength: content.length
        },
      });

      // Split document into chunks using splitDocuments (like working example)
      const textSplitter = new RecursiveCharacterTextSplitter({
        chunkSize: 1000,
        chunkOverlap: 200,
        separators: ['\n\n', '\n', '. ', '! ', '? ', ' ', ''],
      });

      console.log('✂️ Splitting document into chunks...');
      const allSplits = await textSplitter.splitDocuments([document]);
      console.log(`📝 Split document into ${allSplits.length} chunks`);

      if (allSplits.length === 0) {
        throw new Error('No chunks created from document');
      }

      // Add metadata to each chunk
      this.documents = allSplits.map((chunk, index) => new Document({
        pageContent: chunk.pageContent,
        metadata: {
          ...chunk.metadata,
          chunkIndex: index,
          chunkCount: allSplits.length,
        },
      }));

      console.log('📋 Created', this.documents.length, 'document objects with metadata');

      // Create vector store and add documents (like working example)
      console.log('🔄 Creating vector store and adding documents...');
      this.vectorStore = new MemoryVectorStore(this.embeddings);
      await this.vectorStore.addDocuments(this.documents);

      console.log('✅ Vector store created with', this.documents.length, 'document chunks');
      return true;
    } catch (error) {
      console.error('❌ Error processing document for RAG:', error);
      console.error('❌ Error details:', error.message);
      console.error('❌ Error stack:', error.stack);
      return false;
    }
  }

  async searchSimilar(query, k = 4) {
    try {
      if (!this.vectorStore) {
        console.log('❌ No vector store available for search');
        return 'Kein Dokument für die Suche verfügbar.';
      }

      console.log('🔍 Performing similarity search for:', query);
      console.log('📊 Vector store info:', this.getDocumentInfo());

      // Perform similarity search (like working example)
      const results = await this.vectorStore.similaritySearch(query, k);
      
      console.log(`🔍 Raw search results: ${results.length} documents found`);
      results.forEach((doc, i) => {
        console.log(`   ${i + 1}. Content length: ${doc.pageContent.length}, Metadata:`, doc.metadata);
        console.log(`      Preview: "${doc.pageContent.substring(0, 100)}..."`);
      });
      
      if (results.length === 0) {
        return `Keine relevanten Informationen zu "${query}" im Dokument gefunden. Das Dokument enthält ${this.documents.length} durchsuchbare Abschnitte.`;
      }

      console.log(`✅ Found ${results.length} relevant chunks`);

      // Format results with metadata (like working example)
      const formattedResults = results.map((doc, index) => {
        const metadata = doc.metadata || {};
        const chunkInfo = metadata.chunkIndex !== undefined ? 
          ` (Abschnitt ${metadata.chunkIndex + 1}/${metadata.chunkCount})` : '';
        
        return `**Treffer ${index + 1}**${chunkInfo}:\n${doc.pageContent.trim()}`;
      });

      const response = `Relevante Informationen aus "${this.getDocumentInfo().name}":\n\n${formattedResults.join('\n\n---\n\n')}`;
      
      console.log('📄 Formatted response length:', response.length);
      return response;
    } catch (error) {
      console.error('❌ Error during similarity search:', error);
      console.error('❌ Error stack:', error.stack);
      return `Fehler bei der RAG-Suche: ${error.message}. Prüfen Sie die Browser-Konsole für Details.`;
    }
  }

  async searchWithScore(query, k = 3, scoreThreshold = 0.7) {
    try {
      if (!this.vectorStore) {
        return 'Kein Dokument für die Suche verfügbar.';
      }

      console.log('🔍 Performing similarity search with scores for:', query);

      // Perform similarity search with scores
      const results = await this.vectorStore.similaritySearchWithScore(query, k);
      
      // Filter by score threshold
      const filteredResults = results.filter(([doc, score]) => score >= scoreThreshold);
      
      if (filteredResults.length === 0) {
        return `Keine ausreichend relevanten Informationen zu "${query}" gefunden (Mindest-Relevanz: ${scoreThreshold}).`;
      }

      console.log(`✅ Found ${filteredResults.length} relevant chunks with sufficient scores`);

      // Format results with scores
      const formattedResults = filteredResults.map(([doc, score], index) => {
        const metadata = doc.metadata;
        return `**Abschnitt ${index + 1}** (Relevanz: ${(score * 100).toFixed(1)}%)\n(aus ${metadata.source}, Teil ${metadata.chunkIndex + 1}/${metadata.chunkCount})\n\n${doc.pageContent}`;
      });

      return `Relevante Informationen aus dem Dokument:\n\n${formattedResults.join('\n\n---\n\n')}`;
    } catch (error) {
      console.error('❌ Error during similarity search with scores:', error);
      return `Fehler bei der Dokumentensuche: ${error.message}`;
    }
  }

  getDocumentInfo() {
    if (!this.vectorStore || !this.documents.length) {
      return {
        name: null,
        totalChunks: 0,
        isReady: false,
        initialized: this.isInitialized
      };
    }

    const firstDoc = this.documents[0];
    return {
      name: firstDoc?.metadata?.source || 'Unknown',
      totalChunks: this.documents.length,
      isReady: !!this.vectorStore,
      initialized: this.isInitialized,
      totalLength: firstDoc?.metadata?.totalLength || 0,
      type: firstDoc?.metadata?.type || 'unknown'
    };
  }

  clear() {
    this.vectorStore = null;
    this.documents = [];
    console.log('🧹 RAG system cleared');
  }
}

// Export a singleton instance
export const ragSystem = new RAGSystem();
export default ragSystem;